import { LitElement, html, css } from "lit";
import { customElement } from "lit/decorators.js";
import { cache } from "lit/directives/cache.js";
import {
    parseDateString,
    getRange,
    toDateString,
    formatDate,
    dateAdd,
    formatNumber,
    formatEmployedInterval,
    getContractChanges,
    employmentTypeLabel,
    EmployeeSortProperty,
    serializeColumOptions,
    serializeFilters,
} from "@pentacode/core";
import { StateMixin } from "../mixins/state";
import { routeProperty, Routing } from "../mixins/routing";
import { app } from "../init";
import { shared } from "../styles";
import "./month-picker";
import "./avatar";
import "./employees-filter";
import "./spinner";
import "./popover";
import "./scroller";
import "./contract-change";

@customElement("ptc-employees-contracts-all")
export class EmployeesContractsAll extends Routing(StateMixin(LitElement)) {
    routePattern = /^employees\/all\/contracts(\/(?<page>[^/]+))?/;

    get routeTitle() {
        return "Verträge: Alle Mitarbeiter";
    }

    private _pages = ["list", "changes"];

    @routeProperty({ arg: "page" })
    private _page: string = "list";

    private get _employees() {
        return app.getFilteredEmployees();
    }

    async handleRoute() {
        if (!this._pages.includes(this._page)) {
            this.go(`employees/all/contracts/list`);
            return;
        }

        //Invalid date
        if (!this.date || !parseDateString(this.date)) {
            this.go(null, { date: toDateString(new Date()) }, true);
            return;
        }
    }

    static styles = [
        shared,
        css`
            :host {
                --table-border-color: hsl(0deg 0% 89.8%);
                --table-sticky-column-offset: 0;
            }

            th,
            td {
                border: 1px solid var(--table-border-color);
                border-inline-start-width: 0;
                border-block-start-width: 0;
                border-collapse: separate;
                padding: var(--spacing);
            }

            td {
                white-space: nowrap;
                vertical-align: middle;
            }

            th {
                width: min-content;
                min-width: 6rem;
                position: sticky;

                font-weight: bold;
            }

            th[colspan] {
                & > * {
                    position: sticky;
                    inset-inline-start: calc(var(--table-sticky-column-offset) + var(--spacing));
                    inset-inline-end: var(--spacing);
                }
            }

            tbody {
                tr:hover {
                    cursor: pointer;
                    color: var(--color-primary);
                }

                th {
                    font-weight: normal;
                }
            }

            .sticky {
                position: sticky;
                background: var(--color-bg);
                z-index: 1;
                inset-block-start: 0;
                inset-inline-start: 0;

                &::after {
                    content: "";
                    position: absolute;
                    inset-inline-end: -1.1px;
                    inset-block-end: 0;
                    width: 1px;
                    height: 100%;
                    background: var(--table-border-color);
                    pointer-events: none;
                }
            }

            thead.sticky {
                z-index: 2;

                &::after {
                    inset-inline-end: auto;
                    width: 100%;
                    height: 1px;
                }
            }

            ptc-avatar {
                margin: -0.25em 0 -0.25em -0.25em;
            }

            ptc-contract-change {
                padding-left: 0.1em;
            }

            @media print {
                * {
                    font-size: 0.7rem !important;
                }

                .padded {
                    padding: 0.3em !important;
                }

                :host,
                .fullbleed {
                    display: block;
                    position: static !important;
                }

                ptc-scroller {
                    height: auto;
                }

                .employee-wrapper {
                    break-inside: avoid;
                }

                ptc-avatar {
                    display: none;
                }

                .contract-row > :last-child {
                    border-right: none;
                }
            }
        `,
    ];

    private _renderList() {
        if (!app.company) {
            return;
        }
        const bonusTypes = app.company.bonusTypes;
        const benefitTypes = app.company.benefitTypes;

        return html`
            <div class="fullbleed vertical layout">
                <div class="center-aligning spacing horizontal layout border-bottom noprint padded">
                    <button class="small subtle" @click=${() => this.go("employees/all/contracts/changes")}>
                        <i class="list-timeline"></i>
                        Vertragsänderungen
                    </button>

                    <div class="stretch"></div>
                    <button
                        class="slim transparent"
                        title="Vertragsdaten als Excel-Datei Exportieren"
                        @click="${() => {
                            this.go("exports/employee_table_excel", {
                                filters: serializeFilters(app.employeeFilters.filters),
                                columns: serializeColumOptions([
                                    { type: "fullId" },
                                    { type: "employmentType" },
                                    { type: "nominalWeeklyHours" },
                                    { type: "defaultSalary" },
                                    { type: "vacationDays" },
                                    { type: "contract_end" },
                                    { type: "employedDays" },
                                    { type: "bonuses" },
                                    { type: "benefits" },
                                ]),
                            });
                        }}"
                    >
                        <i class="file-excel"></i>
                    </button>

                    <button
                        class="transparent slim"
                        title="Vertragsdaten als PDF-Datei exportieren"
                        @click=${() => print()}
                    >
                        <i class="print"></i>
                    </button>
                </div>

                <ptc-employees-filter
                    class="border-bottom"
                    .sortableProps=${[
                        "firstName",
                        "lastName",
                        "staffNumber",
                        "contractEnd",
                        "employedDays",
                    ] as EmployeeSortProperty[]}
                ></ptc-employees-filter>

                <div class="padded bottom-margined horizontal layout printonly small-caps">
                    <div class="stretch">Vertragsdaten (Stand ${formatDate(new Date())})</div>
                    <div class="subtle">${app.company?.name}</div>
                </div>
                <ptc-scroller class="stretch">
                    <table>
                        <thead class="sticky">
                            <tr>
                                <th scope="col" rowspan="2" class="sticky"></th>
                                <th id="employmentType" scope="col" rowspan="2">Besch.verh.</th>
                                <th id="nominalWeeklyHours" scope="col" rowspan="2">
                                    <div>Sollstunden</div>
                                    <div class="top-margined small subtle">(pro Woche)</div>
                                </th>
                                <th id="defaultSalary" scope="col" rowspan="2">Lohn / Gehalt</th>
                                <th id="vacationDays" scope="col" rowspan="2">
                                    Urlaubsanspr.
                                    <div class="top-margined small subtle">(Tage / Jahr)</div>
                                </th>
                                <th id="latestEnd" scope="col" rowspan="2">Vertragsende</th>
                                <th id="employedInterval" scope="col" rowspan="2">Betriebszugehörigkeit</th>
                                ${bonusTypes.length
                                    ? html`<th id="bonusTypes" scope="colgroup" colspan=${bonusTypes.length}>
                                          <span>Zuschläge</span>
                                      </th>`
                                    : null}
                                ${benefitTypes.length
                                    ? html`<th id="benefitTypes" scope="colgroup" colspan=${benefitTypes.length}>
                                          <span>Sonderleistungen</span>
                                      </th>`
                                    : null}
                            </tr>
                            <tr>
                                ${bonusTypes.map(
                                    (bonusType) =>
                                        html`<th
                                            id="bonusType-${bonusType.id}"
                                            headers="bonusTypes"
                                            scope="col"
                                            class="padded smaller"
                                        >
                                            ${bonusType.name}
                                        </th>`
                                )}
                                ${benefitTypes.map(
                                    (benefitType) =>
                                        html`<th
                                            id="benefitType-${benefitType.id}"
                                            headers="benefitTypes"
                                            scope="col"
                                            class="padded smaller"
                                        >
                                            ${benefitType.name}
                                        </th>`
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            ${this._employees.map((employee) => {
                                const { currentContract, latestContract, employedInterval } = employee;
                                if (!currentContract) {
                                    return;
                                }
                                const { defaultSalary } = currentContract;
                                const latestEnd = latestContract.end;
                                return html`
                                    <tr @click=${() => this.go(`employees/${employee.id}/contracts`)}>
                                        <th id="employee-${employee.id}" scope="row" headers="employee" class="sticky">
                                            <div class="horizontal spacing center-aligning layout">
                                                <ptc-avatar class="tinier" .employee=${employee ?? null}></ptc-avatar>
                                                <div class="stretch ellipsis">
                                                    ${employee.lastName}, ${employee.firstName}
                                                </div>
                                                ${employee.staffNumber
                                                    ? html`<div class="small subtle">#${employee.staffNumber}</div>`
                                                    : ""}
                                            </div>
                                        </th>
                                        <td headers="employee-${employee.id} employmentType">
                                            ${employmentTypeLabel(currentContract.employmentType)}
                                        </td>
                                        <td headers="employee-${employee.id} nominalWeeklyHours">
                                            ${formatNumber(currentContract.nominalWeeklyHours)}
                                        </td>
                                        <td headers="employee-${employee.id} defaultSalary">
                                            ${!defaultSalary
                                                ? ""
                                                : defaultSalary.type === "monthly"
                                                  ? html` ${formatNumber(defaultSalary.amount)} € / Mon `
                                                  : html` ${formatNumber(defaultSalary.amount)} € / Std `}
                                        </td>
                                        <td headers="employee-${employee.id} vacationDays">
                                            ${formatNumber(currentContract.vacationDays)}
                                        </td>
                                        <td headers="employee-${employee.id} latestEnd">
                                            ${latestEnd ? formatDate(latestEnd) : "Unbefristet"}
                                        </td>
                                        <td headers="employee-${employee.id} employedInterval">
                                            ${formatEmployedInterval(employedInterval)}
                                        </td>

                                        ${bonusTypes.map((bonusType) => {
                                            const bonus = currentContract.bonuses.find(
                                                (bonus) => bonus.typeId === bonusType.id
                                            );
                                            return html`<td
                                                headers="employee-${employee.id} bonusType-${bonusType.id} bonusTypes"
                                            >
                                                ${bonus ? `${bonus.percent}%` : ""}
                                            </td>`;
                                        })}
                                        ${benefitTypes.map((benefitType) => {
                                            const benefit = currentContract.benefits.find(
                                                (benefit) => benefit.typeId === benefitType.id
                                            );
                                            return html`<td
                                                headers="employee-${employee.id} benefitType-${benefitType.id} benefitTypes"
                                            >
                                                ${benefit ? `${benefit.amount}€` : ""}
                                            </td>`;
                                        })}
                                    </tr>
                                `;
                            })}
                        </tbody>
                    </table>
                </ptc-scroller>
            </div>
        `;
    }

    private _renderChanges() {
        const date = parseDateString(this.date);

        if (!date) {
            return;
        }

        const range = getRange(this.date, "month");

        const changes = this._employees
            .map((employee) => ({ employee, changes: getContractChanges(app.company!, employee, range) }))
            .filter(({ changes }) => changes.length);

        return html`
            <div class="fullbleed vertical layout">
                <div class="center-aligning padded spacing horizontal layout border-bottom noprint">
                    <button class="transparent slim" @click=${() => this.go("employees/all/contracts/list")}>
                        <i class="chevron-left"></i> Verträge
                    </button>

                    <div class="stretch"></div>

                    <ptc-month-picker
                        @change=${({ detail: { date } }: CustomEvent<{ date: string }>) => this.go(null, { date })}
                        .date=${this.date}
                    ></ptc-month-picker>

                    <div class="stretch"></div>

                    <button class="transparent slim" @click=${() => print()}>
                        <i class="print"></i>
                    </button>
                </div>

                <ptc-employees-filter
                    class="border-bottom"
                    .sortableProps=${["firstName", "lastName", "staffNumber"] as EmployeeSortProperty[]}
                ></ptc-employees-filter>

                <div class="padded horizontaly layout small-caps printonly bottom-margined">
                    <div class="stretch">
                        Vertragsänderungen
                        <strong>${formatDate(range.from)} - ${formatDate(dateAdd(range.to, { days: -1 }))}</strong>
                    </div>
                    <div class="subtle">${app.company?.name}</div>
                </div>

                ${!changes.length
                    ? html`
                          <div class="stretch faded double-padded centering vertical layout">
                              <i class="giant list-timeline"></i>
                              <div>Es liegen keine Vertragsänderungen für diesen Monat vor.</div>
                          </div>
                      `
                    : html`
                          <ptc-scroller class="stretch">
                              ${this._employees.map((employee) => {
                                  const contractChanges = getContractChanges(app.company!, employee, range);
                                  if (!contractChanges.length) {
                                      return;
                                  }
                                  return html`
                                      <div class="padded border-bottom employee-wrapper">
                                          <div
                                              class="padded spacing horizontal center-aligning layout employee-header"
                                              @click=${() => this.go(`employees/${employee.id}/contracts/changes`)}
                                          >
                                              <ptc-avatar .employee=${employee} class="tinier"></ptc-avatar>
                                              <div class="stretch semibold">
                                                  ${employee.name}
                                                  ${employee.staffNumber
                                                      ? html`
                                                            <span class="small subtle">#${employee.staffNumber}</span>
                                                        `
                                                      : ""}
                                              </div>
                                          </div>
                                          ${contractChanges.map(
                                              (change) => html`
                                                  <ptc-contract-change .change=${change}></ptc-contract-change>
                                              `
                                          )}
                                      </div>
                                  `;
                              })}
                          </ptc-scroller>
                      `}
            </div>
        `;
    }

    render() {
        return cache(this._page === "changes" ? this._renderChanges() : this._renderList());
    }

    updated() {
        queueMicrotask(() => {
            // Set sticky column offset based on first column width
            const firstColumn = this.shadowRoot?.querySelector("th.sticky");
            if (firstColumn) {
                const width = firstColumn.getBoundingClientRect().width;
                this.style.setProperty("--table-sticky-column-offset", `${width}px`);
            }
        });
    }
}
